import { queryOptions } from '@tanstack/react-query';

import type {
    SiteRedirectDestination,
    SiteSection,
    SiteSpace,
    SiteStructure,
} from '@gitbook/api-client';
import type { HiveClient } from '@gitbook/hive-client';
import { CONTENT_DEFAULT_BRANCH, type RevisionPage } from '@gitbook/hive-core';
import type { IconDefinition } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { PageIcon, SpaceIcon } from '../../../../../Components';
import { flattenGroupedSections } from '../../../../../state';

type ResolvedSiteRedirectDestination =
    | { siteSection: SiteSection; kind: 'site-section' }
    | { siteSpace: SiteSpace; kind: 'site-space' }
    | { page: RevisionPage; kind: 'site-page' };

export function getPropsFromResolvedDestination(
    resolvedDestination: ResolvedSiteRedirectDestination
): {
    leadingIcon: IconDefinition | null;
    label: string;
} {
    switch (resolvedDestination.kind) {
        case 'site-section':
            return {
                leadingIcon: null,
                label: resolvedDestination.siteSection.title,
            };
        case 'site-space':
            return {
                leadingIcon: <SpaceIcon space={resolvedDestination.siteSpace.space} />,
                label: resolvedDestination.siteSpace.title,
            };
        case 'site-page':
            return {
                leadingIcon: <PageIcon page={resolvedDestination.page} />,
                label: resolvedDestination.page.title,
            };
        default:
            assertNever(resolvedDestination);
    }
}

/**
 * Query to resolve a site redirect destination.
 * If the destination is a site page, it will fetch the page from the Hive client.
 */
export function siteRedirectDestinationResolutionQuery(input: {
    siteStructure: SiteStructure;
    siteRedirectDestination: SiteRedirectDestination | null;
    hiveClient: HiveClient;
}) {
    const { siteStructure, siteRedirectDestination, hiveClient } = input;
    return queryOptions({
        queryKey: ['siteRedirectDestinationResolution', { siteStructure, siteRedirectDestination }],
        queryFn: async () => {
            if (!siteRedirectDestination) {
                return null;
            }
            return resolveSiteRedirectDestination({
                siteStructure,
                siteRedirectDestination,
                hiveClient,
            });
        },
    });
}

/**
 * Resolve a site redirect destination.
 */
async function resolveSiteRedirectDestination(input: {
    siteStructure: SiteStructure;
    siteRedirectDestination: SiteRedirectDestination;
    hiveClient: HiveClient;
}): Promise<ResolvedSiteRedirectDestination | null> {
    const { siteStructure, siteRedirectDestination: destination, hiveClient } = input;
    switch (destination.kind) {
        case 'site-section': {
            const siteSection = resolveSiteSection({
                siteStructure,
                siteSectionId: destination.siteSectionId,
            });
            if (!siteSection) {
                return null;
            }
            return { kind: destination.kind, siteSection };
        }
        case 'site-space': {
            const siteSpace = resolveSiteSpace({
                siteStructure,
                siteSpaceId: destination.siteSpaceId,
            });
            if (!siteSpace) {
                return null;
            }
            return { kind: destination.kind, siteSpace };
        }
        case 'site-page': {
            const page = await resolveSitePage({
                siteStructure,
                siteSpaceId: destination.siteSpaceId,
                pageId: destination.pageId,
                hiveClient,
            });
            if (!page) {
                return null;
            }
            return { kind: destination.kind, page };
        }
        default:
            assertNever(destination);
    }
}

/**
 * Resolve a site section from a site structure.
 */
function resolveSiteSection(input: {
    siteStructure: SiteStructure;
    siteSectionId: string;
}): SiteSection | null {
    const { siteStructure, siteSectionId } = input;
    switch (siteStructure.type) {
        case 'sections':
            return (
                flattenGroupedSections(siteStructure.structure).find(
                    (section) => section.id === siteSectionId
                ) ?? null
            );
        case 'siteSpaces':
            return null;
        default:
            assertNever(siteStructure);
    }
}

/**
 * Resolve a site space from a site structure.
 */
function resolveSiteSpace(input: {
    siteStructure: SiteStructure;
    siteSpaceId: string;
}): SiteSpace | null {
    const { siteStructure, siteSpaceId } = input;
    switch (siteStructure.type) {
        case 'sections':
            return (
                flattenGroupedSections(siteStructure.structure)
                    .flatMap((section) => section.siteSpaces)
                    .find((siteSpace) => siteSpace.id === siteSpaceId) ?? null
            );
        case 'siteSpaces':
            return (
                siteStructure.structure.find((siteSpace) => siteSpace.id === siteSpaceId) ?? null
            );
        default:
            assertNever(siteStructure);
    }
}

/**
 * Resolve a site page from a site structure.
 */
async function resolveSitePage(input: {
    siteStructure: SiteStructure;
    siteSpaceId: string;
    pageId: string;
    hiveClient: HiveClient;
}): Promise<RevisionPage | null> {
    const { siteStructure, siteSpaceId, pageId, hiveClient } = input;
    const siteSpace = resolveSiteSpace({ siteStructure, siteSpaceId });
    if (!siteSpace) {
        return null;
    }

    const sourceRevision = getSuspense(
        hiveClient.store.getBranchRevision(siteSpace.space.id, CONTENT_DEFAULT_BRANCH)
    );

    if (!sourceRevision) {
        return null;
    }

    const { revision } = getSuspense(
        hiveClient.getContentComputer(sourceRevision.contentId).getCompleteRevision(sourceRevision)
    );

    if (!revision) {
        return null;
    }

    return Object.values(revision.pages).find((page) => page.id === pageId) ?? null;
}
